import { animate, style, transition, trigger } from '@angular/animations';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoPipe } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { LottieComponent } from 'ngx-lottie';
import { AppState } from '../../app.reducer';
import { transitionEasing } from '../../constants/transition';
import { CompanionChatService } from '../../services/companion-chat.service';
import { FontawesomeService } from '../../services/fontawesome.service';
import { selectUserAccount } from '../../store/user-account/user-account.reducer';
import { selectUserAvatarUrl } from '../../store/user-avatar/user-avatar.reducer';
import { AvatarPlaceholderComponent } from '../avatar-placeholder/avatar-placeholder.component';

/**
 * Shared KIRA message thread (bubbles, tool calls, typing) used by sidebar and prompting modal.
 */
@Component({
  selector: 'app-companion-chat-thread',
  templateUrl: './companion-chat-thread.component.html',
  styleUrls: ['./companion-chat-thread.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    TranslocoPipe,
    FontAwesomeModule,
    LottieComponent,
    AvatarPlaceholderComponent,
  ],
  animations: [
    trigger('messageAnimation', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'scale(0.9) translateY(10px)',
        }),
        animate(
          `150ms ${transitionEasing}`,
          style({
            opacity: 0.66,
            transform: 'scale(1.02)',
          }),
        ),
        animate(
          `50ms ${transitionEasing}`,
          style({
            opacity: 1,
            transform: 'scale(1)',
          }),
        ),
      ]),
    ]),
  ],
})
export class CompanionChatThreadComponent {
  private readonly store = inject(Store<AppState>);
  public readonly companionChat = inject(CompanionChatService);
  public readonly fontawesomeService = inject(FontawesomeService);
  public readonly domSanitizer = inject(DomSanitizer);

  /**
   * When true, only messages from the current prompting-modal session are shown.
   */
  readonly promptingOnly = input(false);

  readonly avatarUrl = this.store.selectSignal(selectUserAvatarUrl);
  readonly userAccount = this.store.selectSignal(selectUserAccount);

  readonly messages = computed(() =>
    this.promptingOnly()
      ? this.companionChat.promptingMessages()
      : this.companionChat.chat.messages,
  );

  get chat() {
    return this.companionChat.chat;
  }

  get loadingLottie() {
    return this.companionChat.loadingLottie;
  }

  get errorWhileReplying() {
    return this.companionChat.errorWhileReplying;
  }

  /**
   * Handles companionAction / companionRoute clicks.
   * @param {Event} event Click event
   * @returns {void}
   */
  handleMessageClick(event: Event): void {
    this.companionChat.handleMessageClick(event);
  }

  /**
   * Parses a tagged message part.
   * @param {string} message Message text
   * @param {string} tag Tag name
   * @returns {string} Parsed content
   */
  parseMessagePart(message: string, tag: string = 'message'): string {
    return this.companionChat.parseMessagePart(message, tag);
  }

  /**
   * Parses markdown to HTML.
   * @param {string} message Message text
   * @returns {string} HTML string
   */
  parseMarkdown(message: string): string {
    return this.companionChat.parseMarkdown(message);
  }

  getUserInitials(): string {
    const account = this.userAccount();
    return `${account?.firstName?.[0] ?? ''}${account?.lastName?.[0] ?? ''}`.toUpperCase();
  }
}
